/**
 * T17307 · POLL · overdue-fire-then-resume (single fetch, not double).
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1486 (row 28 closure).
 *
 * On resume from background, a polling query's interval-tick MAY have
 * elapsed while the document was hidden. The contract:
 *
 *   1. If the query is OVERDUE (the next-poll timestamp has already
 *      passed), fire EXACTLY ONE `q.fetch()` to catch up — then
 *      schedule the next poll on the normal cadence.
 *
 *   2. Schedule the next poll regardless of overdue status — resuming
 *      polling without scheduling is silent staleness.
 *
 *   3. NEVER fire-and-schedule double: a single resume must not produce
 *      two fetches in flight. The overdue catch-up is the SINGLE fetch;
 *      the normal-cadence schedule then takes over.
 *
 *   4. Not-overdue resume: zero fetches, just schedule.
 *
 * This module is the pure decision surface; the platform-specific
 * resume listeners (visibilitychange:visible, pageshow:persisted=true,
 * AppState='active') call into it after the connectivity FSM has
 * confirmed CONNECTED.
 */

import type { Query } from '@tanstack/react-query';

export interface PollResumeDeps {
  /** Returns true when `query.next-poll-due-at` has elapsed. */
  readonly isOverdue: (query: Query) => boolean;
  /** Triggers a single one-shot fetch on `query`. */
  readonly fetchOnce: (query: Query) => void;
  /** Schedules the next normal-cadence poll for `query`. */
  readonly scheduleNext: (query: Query) => void;
}

/**
 * The decision returned by `decideResume` — pure, testable, no side
 * effects. The orchestrator below interprets it into the two side
 * effects via the dep callbacks.
 */
export interface PollResumeDecision {
  readonly fireCount: 0 | 1;
  readonly schedule: true;
}

/**
 * Pure decision function. Always schedules. Fires exactly once iff
 * the query is overdue. Never fires twice. Never schedules zero polls.
 */
export function decideResume(query: Query, isOverdue: (q: Query) => boolean): PollResumeDecision {
  return {
    fireCount: isOverdue(query) ? 1 : 0,
    schedule: true,
  };
}

/**
 * Orchestrator that applies the decision via the dep callbacks.
 * Wraps `decideResume` so the resume call site is one line.
 */
export function resumePolling(query: Query, deps: PollResumeDeps): void {
  const decision = decideResume(query, deps.isOverdue);
  if (decision.fireCount === 1) {
    deps.fetchOnce(query);
  }
  deps.scheduleNext(query);
}
